// dependencies
import { Child } from 'pym.js';
import Zion from './utils/Zion';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

// polyfills
import './utils/polyfills';

// styles
import './styles/graphic.scss';

/* develblock:start */
import * as d3 from 'd3';
import { format } from 'd3-format';
// import { mean } from 'd3-array';
import meta from './data/meta.json';
import { linearAxis } from '../../../factory/axes';

import './styles/graphic.scss';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  shortMonths: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
});

const fmtComma = format(',');
const fmt1f = format('.1f');

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

let minValue;
let maxValue;
let maxItem;
let maxIndex;
let lastYear;
let currentYear;
let tooltipEl;

// eslint-disable-next-line no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

d3.json('https://ix.cnn.io/data/tornado-locations/deadly_tornado_counts.json').then((jsondata) => {
  const data = jsondata.data.map(d => ({
    ...d,
    // Convert d.year to a string before parsing
    date: d3.timeParse('%Y')(String(d.year)),
  }))
    .sort((a, b) => a.date - b.date);

  // ... rest of your D3 code

  minValue = Math.min(0, d3.min(data, d => d.fatal_storm_deaths));
  const maxActualValue = d3.max(data, d => +d.fatal_storm_deaths);

  maxValue = maxActualValue * 1.2;

  maxItem = data.find(d => +d.fatal_storm_deaths === maxActualValue);
  maxIndex = data.findIndex(d => +d.fatal_storm_deaths === maxActualValue);

  lastYear = dayjs().subtract(1, 'year').format('YYYY');
  currentYear = dayjs().year();


  const tz = 'America/New_York';
  const shortTimestamp = dayjs(jsondata.lastUpdated).tz(tz).format('h a [ET]');
  const medTimestamp = dayjs(jsondata.lastUpdated).tz(tz).format('MMMM D');

  d3.select('p#timestamp')
    .html(`Data collected annually. Last updated ${dayjs(jsondata.lastUpdated).tz('America/New_York').format('MMMM D, YYYY [at] h a [ET]')}.`);

  draw(data, shortTimestamp, medTimestamp);

  d3.select('h2.subhed.dek').html(jsondata.msg);

  window.onresize = () => draw(data, shortTimestamp, medTimestamp);
});

function draw(data, shortTimestamp, medTimestamp) {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;

  const margins = {
    top: 30,
    right: isMobile ? 52 : 50,
    bottom: 25,
    left: isMobile ? 30 : 30,
  };

  const width = initialWidth - margins.right - margins.left;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  // Time scale for the x-axis
  const x = d3.scaleTime()
    .domain(d3.extent(data, d => d.date))
    .range([0, chartWidth]);

  const xTicks = isMobile ? 3 : 6;
  const xAxis = d3.axisBottom(x)
    .ticks(xTicks)
    .tickFormat(d => dayjs(d).format('YYYY'));

  const yAxisConfig = {
    orientation: 'left',
    range: [chartHeight, 0],
    domain: [Math.min(0, minValue), maxValue],
    nice: false,
  };

  const { axis: yAxis, scale: y } = linearAxis(yAxisConfig);

  const yTicks = isMobile ? 5 : 5;
  const leading = 1.2;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${margins.left}, ${margins.top})`);

  const barWidth = chartWidth / data.length * 0.7;

  const xGrid = g.append('g')
    .attr('class', 'x grid')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  xGrid.selectAll('text')
    .attr('y', 10);

  const yGrid = g.append('g')
    .attr('class', 'y grid')
    .call(yAxis(y)
      .ticks(yTicks)
      .tickSize(-width)
      .tickFormat(fmtComma));

  yGrid.selectAll('text')
    .attr('x', -10)
    .filter(d => d === 0 && minValue >= 0);

  function raise() {
    d3.select(this).raise();
  }

  const mobileSize = () => window.innerWidth <= 500;

  // Tooltip function
  const tooltip = (d) => {
    if (!tooltipEl) return;

    if (d) {
      tooltipEl.text(fmtComma(d.fatal_storm_deaths));
      const tooltipElOffset = mobileSize() ? 5 : 8;
      tooltipEl.attr('transform', `translate(${x(d.date) + (barWidth / 2) - tooltipElOffset} ${y(+d.fatal_storm_deaths)})`);
      tooltipEl.style.visibility = 'visible';
    } else {
      tooltipEl.text('');
      tooltipEl.style.visibility = 'hidden';
    }
  };

  const group = g.append('g')
    .attr('class', 'data');

  const bars = group.append('g')
    .attr('class', 'bars');

  const bar = bars.selectAll('.bar')
    .data(data)
    .enter().append('g')
    .attr('class', (d, i) => (i === maxIndex || i === data.length - 1 ? 'bar highlight' : 'bar'))
    .attr('role', 'list')
    .attr('transform', d => `translate(${x(d.date) - barWidth / 2}, ${y(d.fatal_storm_deaths < 0 ? 0 : d.fatal_storm_deaths)})`)
    .on('mouseover', (d, i) => {
      if (i !== maxIndex && i < data.length - 1) {
        tooltip(d);
      } else {
        tooltip();
      }
    })
    .on('mouseout', () => tooltip());

  bar.append('title')
    .text(d => `${d.year} (${fmtComma(d.fatal_storm_deaths)})`);

  bar.append('rect')
    .attr('width', barWidth)
    .attr('height', d => Math.abs(y(d.fatal_storm_deaths) - y(0)));

  if (y.domain()[0] < 0) {
    g.append('line')
      .attr('role', 'presentation')
      .attr('class', 'zero-bar')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', y(0))
      .attr('y2', y(0));
  }

  // g.append('line')
  //   .attr('role', 'presentation')
  //   .attr('class', 'zero-bar historical-mean')
  //   .style('stroke-dasharray', ('3, 4'))
  //   .attr('x1', 0)
  //   .attr('x2', width)
  //   .attr('y1', y(maxValue / 2))
  //   .attr('y2', y(maxValue / 2));

  // g.append('text')
  //   .attr('x', 0)
  //   .attr('y', y(maxValue / 2))
  //   .attr('dy', '-.4em')
  //   .attr('class', 'historical-label halo-sm')
  //   .text('Mean value');

  // Reintroduce annotations
  const drawAnnotation = (item, barWidth, classList, align, toplines) => {
    const xOffsetMobileStart = -4;
    const xOffsetDesktopStart = -12;
    const xOffsetEnd = 2.4;

    const xOffset = align === 'start'
      ? (mobileSize() ? xOffsetMobileStart : xOffsetDesktopStart)
      : xOffsetEnd;

    const textAnchor = align === 'start' ? 'start' : 'end';
    const annotation = g.append('g')
      .attr('class', `annotation halo ${classList}`)
      .attr('transform', `translate(${x(item.date) + barWidth / 2}, ${y(item.fatal_storm_deaths < 0 ? 0 : item.fatal_storm_deaths)})`);

    const annoLine = (barWidth / 2) * -1;
    annotation.append('line')
      .attr('x1', annoLine)
      .attr('y1', -25)
      .attr('x2', annoLine)
      .attr('y2', -1)
      .style('stroke-width', 1)
      .style('stroke', '#999')
      .style('fill', 'none');

    if (toplines) {
      toplines.forEach(
        (d, i) => {
          const cls = `annotation-date ${(d[1]) ? d[1] : ''}`;
          const txt = d[0];
          const yOffset = d[2] || `${-2.5 - ((i + 1) * leading)}em`;
          annotation.append('text')
            .attr('dx', '10px')
            .attr('class', cls)
            .text(txt)
            .attr('dy', yOffset)
            .attr('dx', xOffset)
            .attr('text-anchor', textAnchor);
        },
      );
    }

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-value')
      .text(fmtComma(item.fatal_storm_deaths))
      .attr('dy', '-2.5em')
      .attr('dx', xOffset)
      .attr('text-anchor', textAnchor);
  };

  if (maxIndex < data.length - 1) {
    const align = maxIndex >= data.length / 2 ? 'start' : 'start';
    const annotationHigh = [
      [`outbreak, summer ${maxItem.year}`],
      ['Historic and deadly tornado'],
    ];

    drawAnnotation(maxItem, barWidth, 'max', align, annotationHigh);
  }

  drawAnnotation(data[data.length - 1], barWidth, 'current', 'start', [

    [medTimestamp],
    ['as of '],
    ['deaths,'],
    [currentYear],
    // ['Tornado'],
  ]);

  tooltipEl = svg
    .append('g')
    .attr('transform', `translate(${margins.left} ${margins.top})`)
    .append('text')
    .attr('dy', '-.5em')
    .attr('class', 'value in halo tooltipTxt')
    .attr('text-anchor', 'middle')
    .text('');

  window.pymChild.sendHeight();

  d3.select('#root')
    .attr('class', 'ready');
}
